import { SmartToy } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import updateMyDescription from '@src/APIs/graphQl/Employee/updateMyDescription';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import { setEmployeeDescription } from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export function DescriptionEditor() {
    const theme = useTheme();
    const dispatch = useDispatch();
    const employee = useSelector((state: AppState) => state.employee);
    const offline = useSelector((state: AppState) => state.client.offline);
    const showErrorDialog = useErrorDialog();
    const [descriptionValue, setDescriptionValue] = useState(employee.description || '');
    const [isGenerating, setIsGenerating] = useState(false);
    const subscriptionKeyRef = useRef<string | null>(null);

    const typewriterEffect = useCallback(async (text: string, callback: (text: string) => void) => {
        let currentText = '';
        for (let i = 0; i < text.length; i++) {
            currentText += text[i];
            callback(currentText);
            await new Promise((resolve) => setTimeout(resolve, 30));
        }
    }, []);

    useEffect(() => {
        const subKey = WebSocketClient.subscribe<{ optimizedDescription?: string; key: string; error?: string }>(
            EWebsocketTopics.optimizeDescription,
            async (data) => {
                if (data.error) {
                    console.error('Description optimization error:', data.error);
                    setIsGenerating(false);
                    return;
                }

                if (data.optimizedDescription) {
                    await typewriterEffect(data.optimizedDescription, (text) => {
                        setDescriptionValue(text);
                    });
                    setIsGenerating(false);
                }
            },
        );
        subscriptionKeyRef.current = subKey;

        return () => {
            if (subscriptionKeyRef.current) {
                WebSocketClient.unsubscribe(subscriptionKeyRef.current);
            }
        };
    }, [typewriterEffect]);

    const handleSaveDescription = async (value: string) => {
        try {
            const result = await updateMyDescription(value);
            if (result) {
                dispatch(setEmployeeDescription(value));
                setDescriptionValue(value);
            }
        } catch (ex) {
            showErrorDialog(ex as Error);
            throw ex;
        }
    };

    const handleSaveDescriptionClick = async () => {
        await handleSaveDescription(descriptionValue);
    };

    const handleClearDescription = async () => {
        setDescriptionValue('');
        await handleSaveDescription('');
    };

    const handleGenerateDescription = async () => {
        if (isGenerating || !descriptionValue.trim()) return;

        setIsGenerating(true);
        const originalDescription = descriptionValue;
        setDescriptionValue('');

        await WebSocketClient.sendMessage(EWebsocketTopics.optimizeDescription, {
            description: originalDescription,
            key: `optimize-${Date.now()}`,
        });
    };

    return (
        <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Beschreibung
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                    Info bearbeiten
                </Typography>

                <Box>
                    <FormatedTextInput
                        value={descriptionValue}
                        onChange={(value: string) => setDescriptionValue(value)}
                        readonly={offline || isGenerating}
                        isAI={isGenerating ? true : undefined}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 2,
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<SmartToy />}
                            onClick={handleGenerateDescription}
                            disabled={offline || isGenerating || !descriptionValue.trim()}
                            sx={{
                                textTransform: 'none',
                                background: 'linear-gradient(45deg, #403ddb 30%, #04d5a9 90%)',
                                color: '#ffffff',
                                border: 'none',
                                boxShadow: '0 3px 5px 2px rgba(64, 61, 219, .3)',
                                animation: isGenerating ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #2d2a9e 30%, #03a085 90%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 10px 4px rgba(64, 61, 219, .3)',
                                },
                                '&:disabled': {
                                    background: 'linear-gradient(45deg, #403ddb 30%, #04d5a9 90%)',
                                    color: '#ffffff',
                                    opacity: 0.8,
                                },
                                '@keyframes pulse': {
                                    '0%': {
                                        transform: 'scale(1)',
                                        boxShadow: '0 3px 5px 2px rgba(64, 61, 219, .3)',
                                    },
                                    '50%': {
                                        transform: 'scale(1.05)',
                                        boxShadow: '0 6px 15px 4px rgba(64, 61, 219, .5)',
                                    },
                                    '100%': {
                                        transform: 'scale(1)',
                                        boxShadow: '0 3px 5px 2px rgba(64, 61, 219, .3)',
                                    },
                                },
                            }}
                        >
                            {isGenerating ? 'Generiere...' : 'T4lo Assistant'}
                        </Button>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClearDescription}
                                disabled={offline || !descriptionValue || isGenerating}
                                sx={{ textTransform: 'none' }}
                            >
                                Löschen
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleSaveDescriptionClick}
                                disabled={offline || descriptionValue === employee.description || isGenerating}
                                sx={{ textTransform: 'none' }}
                            >
                                Speichern
                            </Button>
                        </Box>
                    </Box>

                    {offline && (
                        <Box
                            sx={{
                                mt: 1,
                                p: 1,
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>
                                Offline - nicht bearbeitbar
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}
